import { Component, inject, OnInit, ViewChild, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertBoxComponent } from 'src/app/shared/components/alert-box/alert-box.component';
import { SpecialtiesService } from '../../services/specialties/specialties.service';
import { QuestionsService } from '../../services/questions.service';
import { Specialty, SpecialtyQueryParams } from '../../models/specialty.model';
import { QUESTION_STATUS_LABELS, Question, QuestionStatus } from '../../models/question.model';

@Component({
  selector: 'app-question-details',
  standalone: false,
  templateUrl: './question-details.component.html',
  styleUrls: ['./question-details.component.scss'],
})
export class QuestionDetailsComponent implements OnInit {
  questionId!: number;
  question: Question = { id: 0 };
  selectedSpecialization: number | null = null;
  specializations: Specialty[] = [];
  specializationsPage: number = 1;
  specializationsPageCount: number = 1;
  specializationsPageSize: number = 20;
  specializationsSearchInput!: string;
  saveDialogRef: MatDialogRef<AlertBoxComponent> | undefined;

  dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private specialtiesService = inject(SpecialtiesService);
  private questionsService = inject(QuestionsService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('selectSpecializations') selectSpecializations!: NgSelectComponent;
  specializationsSearchTimeout: ReturnType<typeof setTimeout> | undefined;

  ngOnInit(): void {
    this.questionId = Number(this.route.snapshot.paramMap.get('id'));
    this.getSpecializations();
    this.getQuestion();
  }

  getQuestion(): void {
    this.questionsService.getQuestionById(this.questionId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.question = response.data;
        this.selectedSpecialization = response.data.specialization?.id ?? null;
      },
      error: () => { /* keep placeholder question data on failure */ },
    });
  }

  getClientName(): string {
    const client = this.question.client;
    if (!client) return '-';
    return `${client.first_name} ${client.last_name}`.trim() || '-';
  }

  private parseDate(dateString: string): Date | null {
    const usMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})/);
    const date = usMatch
      ? new Date(Number(usMatch[3]), Number(usMatch[1]) - 1, Number(usMatch[2]), Number(usMatch[4]), Number(usMatch[5]))
      : new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  formatDate(dateString?: string | null): string {
    if (!dateString) return '-';
    const date = this.parseDate(dateString);
    if (!date) return dateString;

    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
  }

  formatTime(dateString?: string | null): string {
    if (!dateString) return '';
    const date = this.parseDate(dateString);
    if (!date) return '';

    const pad = (value: number) => String(value).padStart(2, '0');
    const hour24 = date.getHours();
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12;

    return `${pad(hour12)}:${pad(date.getMinutes())} ${period}`;
  }

  getStatusLabel(status?: string): string {
    if (!status) return '-';
    return QUESTION_STATUS_LABELS[status as QuestionStatus] || status;
  }

  getSafetyLabel(value?: string | null): string {
    if (!value) return '-';
    const valueLower = value.toLowerCase();
    if (valueLower === 'passed') return 'اجتياز';
    if (valueLower === 'failed') return 'فشل';
    return value;
  }

  getStatusClass(status?: string): string {
    if (!status) return '';
    const statusLower = status.toLowerCase();

    if (statusLower === QuestionStatus.NeedReview || statusLower.includes('review') || statusLower.includes('مراجعة')) {
      return 'needs-review-status';
    } else if (statusLower === QuestionStatus.Closed || statusLower.includes('مغلق')) {
      return 'closed-status';
    } else if (statusLower === QuestionStatus.Open || statusLower.includes('مفتوح') || statusLower.includes('اجاب')) {
      return 'open-status';
    }
    return 'open-status';
  }

  isQuestionClosed(): boolean {
    return this.getStatusClass(this.question.status) === 'closed-status';
  }

  isQuestionOpen(): boolean {
    return this.getStatusClass(this.question.status) === 'open-status';
  }

  getSpecializations(): void {
    const payload: SpecialtyQueryParams = {
      page: this.specializationsPage,
      page_size: this.specializationsPageSize,
    };
    if (this.specializationsSearchInput) {
      payload['search'] = this.specializationsSearchInput;
    }
    this.specialtiesService.getSpecialties(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.specializations = [...this.specializations, ...(response.data ?? [])];
        this.specializationsPageCount = response.pages_count ?? 1;
      },
      error: () => {
        this.specializations = [];
        this.specializationsPageCount = 1;
      },
    });
  }

  onScrollSpecializationsPagination(): void {
    if (this.specializationsPage < this.specializationsPageCount) {
      this.specializationsPage++;
      this.getSpecializations();
    }
  }

  resetSpecializationsSearch(): void {
    this.specializations = [];
    this.specializationsPage = 1;
    this.specializationsSearchInput = '';
    this.getSpecializations();
  }

  searchSpecializationsText(): void {
    this.specializationsSearchInput = this.selectSpecializations.searchTerm;
    this.specializations = [];
    this.specializationsPage = 1;
    clearTimeout(this.specializationsSearchTimeout);
    let that = this;
    that.specializationsSearchTimeout = setTimeout(() => {
      if (that.specializationsSearchInput != '') {
        that.getSpecializations();
      } else {
        that.specializationsPage = 1;
        that.getSpecializations();
      }
    }, 600);
  }

  onSpecializationChange(specializationId: number): void {
    const previousSpecializationId = this.question.specialization?.id;

    this.questionsService.updateQuestion(this.questionId, { specialization: specializationId }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        console.log('updateQuestion response:', response);
        // Use the already-loaded specializations list rather than the PUT response,
        // since the write endpoint isn't confirmed to echo back the expanded {id, name_ar, name_en} object.
        this.question.specialization = this.specializations.find((s) => s.id === specializationId);
      },
      error: () => {
        this.selectedSpecialization = previousSpecializationId ?? null;
        this.showAlertDialog(false, 'حدث خطأ أثناء حفظ التخصص');
      },
    });
  }

  acceptQuestion(): void {
    this.updateStatus(QuestionStatus.Open, 'حدث خطأ أثناء قبول السؤال');
  }

  closeQuestion(): void {
    this.updateStatus(QuestionStatus.Closed, 'حدث خطأ أثناء إغلاق السؤال');
  }

  private updateStatus(status: QuestionStatus, errorMessage: string): void {
    const previousStatus = this.question.status;

    this.questionsService.updateQuestion(this.questionId, { status }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // Use the known label instead of the PUT response, since its shape for `status` isn't confirmed.
        this.question.status = QUESTION_STATUS_LABELS[status];
      },
      error: () => {
        this.question.status = previousStatus;
        this.showAlertDialog(false, errorMessage);
      },
    });
  }

  private showAlertDialog(success: boolean, message: string): void {
    this.dialog.open(AlertBoxComponent, {
      data: {
        imgSuccess: success,
        message,
        button: 'تم',
      },
    });
  }

  copyClientMobile() {
    const mobile = this.question.client?.mobile;
    if (!mobile) return;
    navigator.clipboard.writeText(mobile).then(() => {
      this.saveDialogRef = this.dialog.open(AlertBoxComponent, {
        data: {
          imgSuccess: true,
          message: 'تم نسخ الرابط',
          button: 'تم',
        },
      });
    });
  }

}
